import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { IconPlayerPlayFilled, IconPlayerPauseFilled, IconMaximize, IconDownload, } from "@tabler/icons-react";
import styles from "./HubVideoPlayer.module.scss";
/** Секунды → "м:сс" (для таймкода). */
function fmt(sec) {
    if (!Number.isFinite(sec) || sec < 0)
        return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
}
/**
 * URL медиа с фрагментом первого кадра (`#t=…`) — чтобы браузер показал КАДР
 * из видео как превью (а не чёрный прямоугольник) до первого запуска. Без
 * атрибута `poster` (превью — сам кадр, а не обложка трека).
 */
function frameSrc(url) {
    if (!url)
        return undefined;
    return url.includes("#") ? url : `${url}#t=0.1`;
}
/**
 * Видео-плеер хаба (видеообложка / караоке) в дизайне сайта: единый контейнер
 * (радиус/тень/контур как у обложки), крупный play-оверлей в фирменном стиле и
 * причёсанная панель управления (play/pause + прогресс + тайминг в стиле
 * BarePlayer) вместо сырых нативных контролов. Превью — кадр из самого видео.
 * Воспроизведение не меняем: обычный `<video>` под капотом.
 */
export const HubVideoPlayer = ({ src, label, onPlay, onDownload, downloading }) => {
    const ref = React.useRef(null);
    const containerRef = React.useRef(null);
    const [playing, setPlaying] = React.useState(false);
    const [current, setCurrent] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const [ready, setReady] = React.useState(false);
    const toggle = React.useCallback(() => {
        const el = ref.current;
        if (!el)
            return;
        if (el.paused)
            void el.play().catch(() => { });
        else
            el.pause();
    }, []);
    const seek = (e) => {
        const el = ref.current;
        if (!el)
            return;
        const t = Number(e.target.value);
        el.currentTime = t;
        setCurrent(t);
    };
    // Полноэкранный режим. Десктоп/Android — фуллскрин КОНТЕЙНЕРА (наши контролы
    // остаются видны); iOS Safari фуллскрин div не умеет — там нативный
    // видео-фуллскрин (webkitEnterFullscreen).
    const toggleFullscreen = React.useCallback(() => {
        const container = containerRef.current;
        const video = ref.current;
        if (document.fullscreenElement) {
            void document.exitFullscreen?.().catch(() => { });
            return;
        }
        if (container?.requestFullscreen) {
            void container.requestFullscreen().catch(() => {
                video?.webkitEnterFullscreen?.();
            });
        }
        else {
            video?.webkitEnterFullscreen?.();
        }
    }, []);
    const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
    const safeCurrent = Math.min(Math.max(0, current), safeDuration || 0);
    const progress = safeDuration ? (safeCurrent / safeDuration) * 100 : 0;
    return (_jsxs("div", { className: styles.player, ref: containerRef, children: [_jsxs("div", { className: styles.stage, children: [_jsx("video", { ref: ref, className: styles.video, src: frameSrc(src), playsInline: true, preload: "metadata", onClick: toggle, onTimeUpdate: (e) => setCurrent(e.currentTarget.currentTime), onLoadedMetadata: (e) => {
                            const d = e.currentTarget.duration;
                            setDuration(Number.isFinite(d) ? d : 0);
                            setReady(true);
                        }, onPlay: () => {
                            setPlaying(true);
                            // Старт видео глушит глобальное аудио (единый источник звука).
                            onPlay?.();
                        }, onPause: () => setPlaying(false), onEnded: () => {
                            setPlaying(false);
                            setCurrent(0);
                        } }), _jsx("button", { type: "button", className: `${styles.overlay} ${playing ? styles.overlayHidden : ""}`, onClick: toggle, "aria-label": playing ? "Пауза" : "Смотреть", tabIndex: playing ? -1 : 0, children: _jsx("span", { className: styles.overlayBadge, children: _jsx(IconPlayerPlayFilled, { size: 26 }) }) })] }), _jsxs("div", { className: styles.controls, children: [_jsx("button", { type: "button", className: styles.playBtn, onClick: toggle, "aria-label": playing ? "Пауза" : (label ?? "Играть"), children: playing ? (_jsx(IconPlayerPauseFilled, { size: 18 })) : (_jsx(IconPlayerPlayFilled, { size: 18 })) }), _jsx("input", { type: "range", min: 0, max: safeDuration || 1, step: 0.1, value: safeCurrent, onChange: seek, className: styles.seek, style: { "--vp-seek-progress": `${progress}%` }, "aria-label": "\u041F\u0435\u0440\u0435\u043C\u043E\u0442\u043A\u0430", disabled: !ready || !safeDuration }), _jsxs("span", { className: styles.time, children: [fmt(safeCurrent), " / ", fmt(safeDuration)] }), onDownload && (_jsx("button", { type: "button", className: styles.dlBtn, onClick: onDownload, disabled: downloading, "aria-label": "\u0421\u043A\u0430\u0447\u0430\u0442\u044C", children: _jsx(IconDownload, { size: 18 }) })), _jsx("button", { type: "button", className: styles.fsBtn, onClick: toggleFullscreen, "aria-label": "\u041F\u043E\u043B\u043D\u044B\u0439 \u044D\u043A\u0440\u0430\u043D", children: _jsx(IconMaximize, { size: 18 }) })] })] }));
};
export default HubVideoPlayer;
